import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Home } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activating, setActivating] = useState(false);

  const paymentId = searchParams.get('payment_id');
  const reference = searchParams.get('reference');
  const status = searchParams.get('status');
  const isFallback = searchParams.get('fallback') === 'true';

  useEffect(() => {
    const activateSubscription = async () => {
      if ((status === 'completed' || isFallback) && !activating) {
        setActivating(true);

        try {
          // For fallback/old subscriptions, use activate-demo endpoint
          if (isFallback || (!reference && !paymentId)) {
            const response = await api.post('/subscriptions/activate-demo');
            if (response?.success) {
              toast.success('Subscription activated successfully!', {
                duration: 5000,
                icon: '🎉'
              });
            } else {
              toast.error('Failed to activate subscription. Please contact support.');
            }
          } else {
            // For new HitPay payments, use activate-payment endpoint
            const response = await api.post('/subscriptions/activate-payment', {
              reference: reference,
              paymentId: paymentId,
              status: 'completed'
            });

            if (response?.success) {
              toast.success('Subscription activated successfully!', {
                duration: 5000,
                icon: '🎉'
              });
            } else {
              toast.error('Failed to activate subscription. Please contact support.');
            }
          }
        } catch (error) {
          console.error('Activation error:', error);
          toast.error('Failed to activate subscription');
        }
      }
    };

    activateSubscription();
  }, [status, reference, paymentId, isFallback, activating]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="flex justify-center mb-6"
        >
          <div className="bg-green-100 rounded-full p-6">
            <CheckCircle className="text-green-600" size={64} />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-center text-gray-900 mb-4"
        >
          Subscription Activated!
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center text-gray-600 mb-8"
        >
          Thank you for subscribing! Your subscription is now active and you can start enjoying all the benefits.
        </motion.p>

        {/* Payment Info */}
        {paymentId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gray-50 rounded-xl p-4 mb-6"
          >
            <p className="text-sm text-gray-600 mb-1">Payment ID</p>
            <p className="text-sm font-mono text-gray-900 break-all">{paymentId}</p>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-3"
        >
          <button
            onClick={() => navigate('/subscription/dashboard')}
            className="w-full bg-orange-600 text-white py-4 rounded-xl font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
          >
            Manage Subscription
            <ArrowRight size={20} />
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Back to Home
          </button>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 text-center"
        >
          <p className="text-sm text-gray-500">
            A confirmation email has been sent to your registered email address.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;